<script setup>
import { onMounted, onBeforeUnmount, ref, onBeforeMount, inject, computed } from 'vue'
import WOW from 'wow.js'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import { XMarkIcon } from '@heroicons/vue/20/solid'
import { ShieldCheckIcon } from '@heroicons/vue/24/solid'
import {
  MoonIcon,
  SunIcon,
  Bars3Icon,
  ShoppingBagIcon,
  MapPinIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ArrowRightIcon,
  Squares2X2Icon,
  TagIcon,
  ShoppingCartIcon,
  UserIcon
} from '@heroicons/vue/24/outline'
import placeholderImg from '@/assets/images/placeholder_img.png'

const IS_MOBILE_APP = inject('IS_MOBILE_APP')
const restoStore = useRestoGLobalStore()
const cartStore = useCartGLobalStore()
const authStore = useAuthGlobalStore()
const dynamicContent = restoStore?.restoInfo?.dynamic_site_content
const address = `${restoStore?.restoInfo?.address}`
const name = `${restoStore?.restoInfo?.name}`
const query = `${name} ${address}`
const mapSrc = `https://maps.google.com/maps?q=` + encodeURI(query) + `&output=embed`
const googleAnalyticsKey = restoStore?.SITE_SETTINGS?.google_analytics_key
const isLoggedIn = computed(() => authStore?.isLoggedIn)

const date = new Date()
const currentYear = date.getFullYear()
const isSticky = ref(false)
const showMobileHeader = ref(false)
const bgColor1 = ref('#0a4363')
const activeCategoryId = ref(null)
const activeBottomTab = ref('explore')

const isDarkMode = ref(false)
const showOfferModal = ref(false)

const setBottomTab = (tab) => {
  activeBottomTab.value = tab
}

const isGiftVoucherCategory = (category) => {
  const categoryName = category?.category_name?.toLowerCase?.() || ''
  return categoryName === 'gift voucher' || categoryName === 'gift vouchers'
}

const menuCategories = computed(() => {
  const categories = restoStore?.MENU_DATA?.categories || []
  return categories.filter((category) => category && !isGiftVoucherCategory(category))
})

const categoryPills = computed(() => menuCategories.value.slice(0, 10))
const iconCategories = computed(() => menuCategories.value.slice(0, 6))

const popularDishes = computed(() => {
  const products = restoStore?.restoInfo?.popular_menu?.products || []
  return products
    .filter((product) => product)
    .slice(0, 8)
    .map((product) => {
      const availableItem = (product.items || []).find((item) => item?.is_available != 0)
      const fallbackItem = product.items?.[0]
      const item = availableItem || fallbackItem
      const image = product.menu_img_url || item?.menu_img_url || placeholderImg
      const variants = (product.items || [])
        .map((variant) => variant?.item_syn)
        .filter(Boolean)

      return {
        id: product.product_id,
        name: product.product_name || item?.item_name_full || item?.item_name,
        info: product.info || '',
        image,
        price: item?.price,
        variants,
        isPopular: product.is_popular == 1
      }
    })
})

const activeOffers = computed(() => {
  if (!IS_MOBILE_APP && dynamicContent?.info?.active_website_offers?.length > 0) {
    return dynamicContent.info.active_website_offers
  }
  if (IS_MOBILE_APP && dynamicContent?.info?.active_mobileapp_offers?.length > 0) {
    return dynamicContent.info.active_mobileapp_offers
  }
  return []
})

const primaryOffer = computed(() => activeOffers.value[0] || null)

const cartItemCount = computed(() => {
  return Number(cartStore?.CART_DATA?.order_total_item_quantity || 0)
})

const displayAddress = computed(() => {
  return dynamicContent?.info?.address || address || 'Find us nearby'
})

const selectCategory = (categoryId) => {
  activeCategoryId.value = categoryId
}

const initializeTheme = () => {
  const storedTheme = localStorage.getItem('theme')
  let themePreference = null

  if (storedTheme) {
    try {
      const themeObj = JSON.parse(storedTheme)
      themePreference = themeObj.theme
    } catch (e) {
      console.error('Error parsing theme from localStorage')
      themePreference = 'dark'
    }
  }

  if (themePreference === 'light') {
    isDarkMode.value = false
    document.documentElement.classList.remove('dark')
  } else {
    isDarkMode.value = true
    document.documentElement.classList.add('dark')

    if (!storedTheme) {
      localStorage.setItem('theme', JSON.stringify({ theme: 'dark' }))
    }
  }
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value

  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', JSON.stringify({ theme: 'dark' }))
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', JSON.stringify({ theme: 'light' }))
  }
}

const handleScroll = () => {
  isSticky.value = window.scrollY > 50
}
const handleShowHeader = () => {
  showMobileHeader.value = !showMobileHeader.value
}

const handleNavLinkClick = () => {
  showMobileHeader.value = false
}

const closeOfferModal = () => {
  showOfferModal.value = false
}

const initializeOfferModal = () => {
  if (
    dynamicContent?.content?.rsc_dynamicsite_popup_enabled &&
    dynamicContent?.content?.rsc_dynamicsite_popup_image_url
  ) {
    setTimeout(() => {
      showOfferModal.value = true
    }, 2000)
  }
}

const loadThemeStyle = async (styleLoader) => {
  try {
    await styleLoader()
  } catch (error) {
    window.dispatchEvent(new Event('vite:preloadError'))
  }
}

onBeforeMount(() => {
  initializeTheme()

  if (dynamicContent?.mobile_style == 1) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template2/css/style1.css'))
    bgColor1.value = '#0a4363'
  } else if (dynamicContent?.mobile_style == 2) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template2/css/style2.css'))
    bgColor1.value = '#ce1212'
  } else if (dynamicContent?.mobile_style == 3) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template2/css/style3.css'))
    bgColor1.value = '#df6853'
  } else if (dynamicContent?.mobile_style == 4) {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template2/css/style4.css'))
    bgColor1.value = '#ffa323'
  } else {
    loadThemeStyle(() => import('@/layouts/mobileAppSite/template2/css/style1.css'))
    bgColor1.value = '#0a4363'
  }
})
onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  new WOW().init()
  const favicon = document.querySelector('link[rel="icon"]')
  if (favicon) {
    favicon.href = `${dynamicContent?.info?.logo}`
  } else {
    const newFavicon = document.createElement('link')
    newFavicon.rel = 'icon'
    newFavicon.type = 'image/x-icon'
    newFavicon.href = `${dynamicContent?.info?.logo}`
    document.head.appendChild(newFavicon)
  }

  if (googleAnalyticsKey) {
    const script = document.createElement('script')
    script.src = `https://www.googletagmanager.com/gtm.js?id=${googleAnalyticsKey}`
    document.head.appendChild(script)
    const noscript = document.createElement('noscript')
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${googleAnalyticsKey}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
    document.head.appendChild(noscript)
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', initializeTheme)

  initializeOfferModal()
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.removeEventListener('change', initializeTheme)
})
</script>
<template>
  <div
    class="temp-w2 food-app-t2 transition-colors duration-300 bg-[#f7f8fa] dark:bg-gray-950"
    ref="mobileMenuRef"
  >
    <!-- App header -->
    <header
      :class="{ 'sticky-header': isSticky }"
      class="bg-white/95 dark:bg-gray-900/95 sticky top-0 z-50 relative pt-safe transition-colors duration-300 border-b border-slate-100 dark:border-gray-800 backdrop-blur-md"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3 min-w-0">
            <img
              :src="dynamicContent?.info?.logo"
              alt="Logo"
              class="h-11 w-11 rounded-2xl object-cover ring-1 ring-black/5 shadow-sm"
            />
            <div class="min-w-0 hidden sm:block">
              <p class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Order from
              </p>
              <div class="flex items-center gap-1.5 min-w-0">
                <MapPinIcon class="hidden sm:block h-4 w-4 shrink-0 accent-icon" />
                <p class="truncate text-sm font-bold text-slate-900 dark:text-white">
                  {{ dynamicContent?.info?.name || name }}
                </p>
              </div>
              <p
                class="hidden sm:block truncate text-xs text-slate-500 dark:text-gray-400 max-w-xs"
              >
                {{ displayAddress }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <button
              @click="toggleDarkMode"
              class="p-2.5 rounded-2xl bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 transition"
              aria-label="Toggle dark mode"
            >
              <SunIcon v-if="isDarkMode" class="h-5 w-5 text-amber-400" />
              <MoonIcon v-else class="h-5 w-5 text-slate-700" />
            </button>

            <router-link
              v-if="restoStore?.MENU_URL"
              :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
              class="relative p-2.5 rounded-2xl bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 transition"
              aria-label="View cart"
            >
              <ShoppingBagIcon class="h-5 w-5 text-slate-800 dark:text-gray-200" />
              <span v-if="cartItemCount > 0" class="cart-badge">{{ cartItemCount }}</span>
            </router-link>

            <router-link
              v-if="restoStore?.MENU_URL"
              class="inline-flex btn items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl text-white text-xs sm:text-sm font-semibold"
              :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
            >
              <span class="whitespace-nowrap">Order Online</span>
            </router-link>

            <button class="md:hidden p-1" @click="handleShowHeader()" aria-label="Open menu">
              <Bars3Icon
                v-if="!showMobileHeader"
                class="h-6 w-6 text-slate-900 dark:text-gray-200"
              />
              <XMarkIcon v-else class="h-6 w-6 text-slate-900 dark:text-gray-200" />
            </button>
          </div>
        </div>

        <nav class="hidden items-center gap-7 mt-3 pb-1">
          <a href="#home" class="nav-link">Home</a>
          <a href="#categories" class="nav-link">Menu</a>
          <a href="#hours" class="nav-link">Hours</a>
          <router-link
            v-if="restoStore?.MENU_URL"
            class="nav-link"
            :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
            >Online Order</router-link
          >
          <a href="#find-us" class="nav-link">Find Us</a>
          <router-link
            v-if="dynamicContent?.info?.is_reservation_active"
            class="btn px-5 py-2 rounded-2xl text-white text-sm font-semibold"
            :to="{ name: 'reservation', params: { resto_id: restoStore?.RESTO_ID } }"
            >Book Table</router-link
          >
        </nav>
      </div>

      <div
        :class="[showMobileHeader ? 'block' : 'hidden']"
        class="md:hidden mobile-menu bg-white dark:bg-gray-900 absolute left-0 right-0 top-full pb-4 border-t border-slate-100 dark:border-gray-800 shadow-lg"
      >
        <a href="#home" class="mobile-link" @click="handleNavLinkClick">Home</a>
        <a href="#categories" class="mobile-link" @click="handleNavLinkClick">Menu</a>
        <a href="#hours" class="mobile-link" @click="handleNavLinkClick">Hours</a>
        <router-link
          v-if="restoStore?.MENU_URL"
          class="mobile-link"
          :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
          >Online Order</router-link
        >
        <a href="#find-us" class="mobile-link" @click="handleNavLinkClick">Find Us</a>
        <router-link
          v-if="dynamicContent?.info?.is_reservation_active"
          class="btn rounded-2xl text-white text-sm font-semibold py-2.5 px-4 mx-4 mt-3 inline-block"
          :to="{ name: 'reservation', params: { resto_id: restoStore?.RESTO_ID } }"
          >Book Table</router-link
        >
      </div>
    </header>

    <!-- Notice -->
    <div
      v-if="dynamicContent?.content?.rsc_resto_note_customer_message"
      class="container-fluid pt-1 px-3 bg-white dark:bg-gray-900 border-b border-slate-100 dark:border-gray-800"
    >
      <div class="top-notice-bar">
        <div class="flex">
          <div
            class="flex-none w-1/6 md:w-1/12 text-slate-800 dark:text-gray-200 font-semibold"
          >
            Notice!
          </div>
          <div class="flex-grow w-5/6 md:w-11/12 text-slate-700 dark:text-gray-300">
            <marquee
              class="hover:cursor-pointer"
              onmouseover="this.stop();"
              onmouseout="this.start();"
            >
              {{ dynamicContent?.content?.rsc_resto_note_customer_message }}
            </marquee>
          </div>
        </div>
      </div>
    </div>

    <main class="px-safe dark:text-gray-200 transition-colors duration-300 pb-4 md:pb-0">
      <section id="home" class="container mx-auto px-4 sm:px-6 lg:px-8 pt-5 md:pt-8">
       

        <!-- Category pills -->
        <div
          v-if="categoryPills.length"
          id="categories"
          class="flex gap-2 overflow-x-auto py-4 hide-scrollbar"
        >
          <component
            :is="restoStore?.MENU_URL ? 'router-link' : 'button'"
            v-for="category in categoryPills"
            :key="`pill-${category.category_id}`"
            v-bind="
              restoStore?.MENU_URL
                ? { to: { name: 'menu', params: { menu_slug: restoStore.MENU_URL } } }
                : { type: 'button' }
            "
            class="pill"
            :class="{ 'is-active': activeCategoryId === category.category_id }"
            @click="selectCategory(category.category_id)"
          >
            {{ category.category_name }}
          </component>
        </div>

        <!-- Promo banner -->
        <div
          id="offers"
          class="promo-card mt-1 mb-6"
          :style="{ backgroundImage: `url(${placeholderImg})` }"
        >
          <div class="promo-overlay" aria-hidden="true"></div>
          <div class="promo-copy">
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 mb-2">
              Special offer
            </p>
            <h1 class="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-2">
              {{ dynamicContent?.info?.name }}
            </h1>
            <p v-if="primaryOffer" class="text-sm sm:text-base text-white/90 mb-4 line-clamp-2">
              {{ primaryOffer }}
            </p>
            <p v-else class="text-sm sm:text-base text-white/90 mb-4">
              Fresh flavours delivered fast — order online in a few taps.
            </p>
            <div class="flex flex-wrap gap-2">
              <router-link
                v-if="restoStore?.MENU_URL"
                class="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-2xl text-sm font-bold shadow-md hover:bg-slate-50 transition"
                :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
              >
                Order Now
                <ArrowRightIcon class="h-4 w-4" />
              </router-link>
              <router-link
                v-if="dynamicContent?.info?.is_reservation_active"
                class="inline-flex items-center px-5 py-2.5 rounded-2xl text-sm font-semibold text-white border border-white/40 bg-white/10 hover:bg-white/20 transition"
                :to="{ name: 'reservation', params: { resto_id: restoStore?.RESTO_ID } }"
                >Book Table</router-link
              >
            </div>
            <div v-if="activeOffers.length > 1" class="flex gap-1.5 mt-4">
              <span
                v-for="(_, index) in activeOffers.slice(0, 4)"
                :key="index"
                class="h-1.5 rounded-full transition-all"
                :class="index === 0 ? 'w-5 bg-white' : 'w-1.5 bg-white/40'"
              ></span>
            </div>
          </div>
        </div>

        <!-- Circular categories -->
        <div v-if="iconCategories.length" class="mb-8">
          <div class="flex items-end justify-between mb-4">
            <h2 class="section-title">Categories</h2>
            <router-link
              v-if="restoStore?.MENU_URL"
              class="text-sm font-bold accent-text"
              :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
              >See all</router-link
            >
          </div>
          <div class="flex gap-4 overflow-x-auto hide-scrollbar pb-1">
            <component
              :is="restoStore?.MENU_URL ? 'router-link' : 'button'"
              v-for="category in iconCategories"
              :key="`icon-${category.category_id}`"
              v-bind="
                restoStore?.MENU_URL
                  ? { to: { name: 'menu', params: { menu_slug: restoStore.MENU_URL } } }
                  : { type: 'button' }
              "
              class="icon-cat"
              @click="selectCategory(category.category_id)"
            >
              <span class="icon-cat-avatar">
                {{ (category.category_name || '?').charAt(0) }}
              </span>
              <span class="icon-cat-label">{{ category.category_name }}</span>
            </component>
          </div>
        </div>

        <!-- Popular dishes cards -->
        <div v-if="popularDishes.length" id="popular" class="mb-10">
          <div class="flex items-end justify-between mb-4">
            <div>
              <p class="section-eyebrow">Tasty picks</p>
              <h2 class="section-title">Popular near you</h2>
            </div>
            <router-link
              v-if="restoStore?.MENU_URL"
              class="btn hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-sm font-semibold"
              :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
            >
              Order Online
            </router-link>
          </div>

          <div class="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-1 px-1 md:grid md:grid-cols-2 lg:grid-cols-4 md:overflow-visible">
            <article
              v-for="(dish, index) in popularDishes"
              :key="dish.id || index"
              class="kitchen-card"
            >
              <div class="kitchen-media">
                <img :src="dish.image" :alt="dish.name" loading="lazy" />
                <span v-if="dish.isPopular" class="kitchen-badge">Popular</span>
              </div>
              <div class="kitchen-body">
                <div class="flex items-start gap-3">
                  <img
                    :src="dynamicContent?.info?.logo || placeholderImg"
                    alt=""
                    class="h-9 w-9 rounded-full object-cover ring-2 ring-white dark:ring-gray-800 -mt-7 relative z-10 shadow"
                  />
                  <div class="min-w-0 flex-1 pt-1">
                    <h3 class="font-bold text-slate-900 dark:text-white truncate">
                      {{ dish.name }}
                    </h3>
                    <p v-if="dish.info" class="text-xs text-slate-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                      {{ dish.info }}
                    </p>
                  </div>
                </div>
                <div class="flex items-center justify-between gap-2 mt-3">
                  <p v-if="dish.price != null" class="text-base font-extrabold accent-text">
                    £{{ dish.price }}
                  </p>
                  <router-link
                    v-if="restoStore?.MENU_URL"
                    class="btn px-3 py-1.5 rounded-xl text-white text-xs font-bold"
                    :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
                    >Order</router-link
                  >
                </div>
              </div>
            </article>
          </div>
        </div>

      </section>

      <!-- Opening hours -->
      <section
        v-if="dynamicContent?.info?.resto_opening_times?.consolidated?.length"
        id="hours"
        class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14"
      >
        <div class="hours-panel">
          <div class="hours-panel-header">
            <span class="hours-icon-wrap">
              <ClockIcon class="h-7 w-7 text-white" />
            </span>
            <div>
              <p class="section-eyebrow mb-1">Plan your visit</p>
              <h2 class="section-title">Opening hours</h2>
            </div>
          </div>
          <div class="grid sm:grid-cols-2 gap-3">
            <div
              v-for="openTime in dynamicContent?.info?.resto_opening_times?.consolidated"
              :key="openTime?.day_number || openTime?.day_numbers"
              class="hours-item"
            >
              <p class="font-bold text-slate-900 dark:text-white text-sm">{{ openTime.days }}</p>
              <p class="text-sm text-slate-600 dark:text-gray-300 mt-1">
                <span v-for="(time, index) in openTime.am_pm_times" :key="index">
                  {{ time
                  }}<span v-if="index < openTime.am_pm_times.length - 1">,&nbsp;</span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Find us / Map -->
      <section
        id="find-us"
        class="container mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14"
      >
        <div class="map-panel">
          <div class="map-panel-copy">
            <p class="section-eyebrow">Location</p>
            <h2 class="section-title mb-4">Find us</h2>
            <p class="text-slate-600 dark:text-gray-300 mb-6 leading-relaxed">
              Visit us or call ahead — we’re ready when you are.
            </p>
            <div class="space-y-4 mb-6">
              <div
                v-if="displayAddress"
                class="flex items-start gap-3 text-slate-700 dark:text-gray-200"
              >
                <span class="strip-icon"><MapPinIcon class="h-5 w-5" /></span>
                <span class="pt-2 font-semibold">{{ displayAddress }}</span>
              </div>
              <a
                v-if="dynamicContent?.info?.phone_number"
                :href="`tel:${dynamicContent?.info?.phone_number}`"
                class="flex items-center gap-3 text-slate-700 dark:text-gray-200 hover:opacity-80 transition"
              >
                <span class="strip-icon"><PhoneIcon class="h-5 w-5" /></span>
                <span class="font-semibold">{{ dynamicContent?.info?.phone_number }}</span>
              </a>
            </div>
            <div class="flex flex-wrap gap-3">
              <a
                :href="`https://maps.google.com/maps?q=${encodeURIComponent(query)}`"
                target="_blank"
                rel="noopener noreferrer"
                class="btn inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-white text-sm font-semibold"
              >
                <MapPinIcon class="h-4 w-4" />
                Open in Maps
              </a>
              <router-link
                v-if="restoStore?.MENU_URL"
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-semibold border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800 transition"
                :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
              >
                <ShoppingBagIcon class="h-4 w-4" />
                Order Online
              </router-link>
            </div>
          </div>
          <div class="map-frame wow zoomIn general_grow animated">
            <iframe
              width="100%"
              height="100%"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              :src="mapSrc"
              title="Restaurant location map"
              class="absolute inset-0 h-full w-full border-0"
            >
            </iframe>
          </div>
        </div>
      </section>

      <!-- Food hygiene -->
      <section
        v-if="dynamicContent?.content?.rsc_food_hygiene_rating"
        id="hygiene"
        class="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16"
      >
        <div class="hygiene-card">
          <div class="hygiene-badge-wrap">
            <ShieldCheckIcon class="h-8 w-8 text-white" />
          </div>
          <div class="hygiene-copy">
            <p class="section-eyebrow mb-1">Food standards</p>
            <h2 class="section-title mb-3">Food Hygiene Rating</h2>
            <p
              v-if="dynamicContent?.content?.rsc_allergy_text_1"
              class="text-sm text-slate-600 dark:text-gray-300 mb-1"
            >
              {{ dynamicContent?.content?.rsc_allergy_text_1 }}
            </p>
            <p
              v-if="dynamicContent?.content?.rsc_allergy_text_2"
              class="text-sm text-slate-600 dark:text-gray-300 mb-4"
            >
              {{ dynamicContent?.content?.rsc_allergy_text_2 }}
            </p>
            <a
              class="btn inline-flex items-center px-5 py-2.5 rounded-2xl text-white text-sm font-semibold"
              href="https://ratings.food.gov.uk/"
              target="_blank"
              >For more details</a
            >
          </div>
          <div class="hygiene-rating">
            <img
              v-if="dynamicContent?.content?.rsc_food_hygiene_rating == 3"
              src="./../../dynamicSite/common/images/food-rating-3.svg"
              class="w-full max-w-[220px] mx-auto"
              alt="Food hygiene rating 3"
            />
            <img
              v-else-if="dynamicContent?.content?.rsc_food_hygiene_rating == 4"
              src="./../../dynamicSite/common/images/food-rating-4.svg"
              class="w-full max-w-[220px] mx-auto"
              alt="Food hygiene rating 4"
            />
            <img
              v-else
              src="./../../dynamicSite/common/images/food-rating-5.svg"
              class="w-full max-w-[220px] mx-auto"
              alt="Food hygiene rating"
            />
          </div>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <footer class="app-footer pb-safe">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
        <img
          :src="dynamicContent?.info?.logo"
          alt="Logo"
          class="h-14 w-14 rounded-2xl object-cover mx-auto mb-5 shadow-md ring-1 ring-black/5"
        />
        <p class="text-base font-bold text-white mb-1">
          {{ dynamicContent?.info?.name || name }}
        </p>
        <p v-if="displayAddress" class="text-sm text-white/70 max-w-md mx-auto mb-5">
          {{ displayAddress }}
        </p>
        <div class="flex items-center justify-center gap-3 mb-6">
          <a
            v-if="dynamicContent?.content?.rsc_social_media_link_facebook"
            :href="dynamicContent.content.rsc_social_media_link_facebook"
            target="_blank"
            class="footer-social"
            aria-label="Facebook"
          >
            <i class="fa fa-facebook" aria-hidden="true"></i>
          </a>
          <a
            v-if="dynamicContent?.content?.rsc_social_media_link_instagram"
            :href="dynamicContent.content.rsc_social_media_link_instagram"
            target="_blank"
            class="footer-social"
            aria-label="Instagram"
          >
            <i class="fa fa-instagram" aria-hidden="true"></i>
          </a>
          <a
            v-if="dynamicContent?.content?.rsc_social_media_link_twitter"
            :href="dynamicContent.content.rsc_social_media_link_twitter"
            target="_blank"
            class="footer-social"
            aria-label="Twitter"
          >
            <i class="fa fa-twitter" aria-hidden="true"></i>
          </a>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-4 text-xs font-semibold uppercase tracking-wide text-white/70 mb-5">
          <a href="#home" class="hover:text-white transition">Home</a>
          <a href="#hours" class="hover:text-white transition">Hours</a>
          <a href="#find-us" class="hover:text-white transition">Find Us</a>
          <router-link
            v-if="restoStore?.MENU_URL"
            :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
            class="hover:text-white transition"
            >Menu</router-link
          >
        </div>
        <p class="text-sm text-white/55">
          &COPY; {{ currentYear }} - All rights reserved
        </p>
      </div>
    </footer>

    <!-- Mobile bottom navigation -->
    <nav
      class="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800 pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
      aria-label="Primary"
    >
      <div class="grid grid-cols-4 h-[4.25rem]">
        <a
          href="#home"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'explore' }"
          @click="setBottomTab('explore')"
        >
          <Squares2X2Icon class="h-6 w-6" />
          <span>Explore</span>
        </a>
        <router-link
          v-if="restoStore?.MENU_URL"
          :to="{ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } }"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'search' }"
          @click="setBottomTab('search')"
        >
          <MagnifyingGlassIcon class="h-6 w-6" />
          <span>Menu</span>
        </router-link>
        <a
          v-else
          href="#categories"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'search' }"
          @click="setBottomTab('search')"
        >
          <MagnifyingGlassIcon class="h-6 w-6" />
          <span>Search</span>
        </a>
        <a
          href="#offers"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'deals' }"
          @click="setBottomTab('deals')"
        >
          <TagIcon class="h-6 w-6" />
          <span>Deals</span>
        </a>
        <router-link v-if="isLoggedIn"
          :to="{ name: 'my_orders' }"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'inbox' }"
          @click="setBottomTab('inbox')"
        >
          <ShoppingCartIcon class="h-6 w-6" />
          <span>My Orders</span>
        </router-link>
        <router-link v-else
          :to="{ name: 'login' }"
          class="bottom-tab"
          :class="{ 'is-active': activeBottomTab === 'login' }"
          @click="setBottomTab('login')"
        >
          <UserIcon class="h-6 w-6" />
          <span>Login</span>
        </router-link>
      </div>
    </nav>

    <a
      href="#"
      class="btn-1 fixed bottom-24 md:bottom-6 right-6 p-3 rounded-full shadow-lg text-white z-30"
      aria-label="Back to top"
    >
      <i class="fa fa-chevron-up"></i>
    </a>

    <!-- Offer Modal -->
    <div
      v-if="showOfferModal"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      @click.self="closeOfferModal"
    >
      <div
        class="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl md:max-w-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-fade-in-up"
      >
        <button
          @click="closeOfferModal"
          class="absolute top-3 right-3 z-10 p-1 rounded-full bg-gray-900 bg-opacity-70 text-white hover:bg-opacity-90 transition"
          aria-label="Close offer"
        >
          <XMarkIcon class="md:h-6 md:w-6 h-5 w-5" />
        </button>
        <div class="relative">
          <img
            :src="dynamicContent?.content?.rsc_dynamicsite_popup_image_url"
            :alt="dynamicContent?.info?.name + ' Special Offer'"
            class="w-full h-auto object-cover"
            @error="closeOfferModal"
          />
          <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
            <h3 class="text-white md:text-lg text-base font-bold">
              {{ dynamicContent?.info?.name }}
            </h3>
            <p
              v-if="dynamicContent?.content?.rsc_dynamicsite_popup_text_msg"
              class="text-white md:text-sm text-xs opacity-90"
            >
              {{ dynamicContent?.content?.rsc_mobileapp_popup_text_msg }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '@/layouts/dynamicSite/template2/css/responsive.css';
@import 'https://maxcdn.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css';
@import 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';

:root {
  color-scheme: light dark;
}

.food-app-t2 {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}

.nav-link {
  @apply text-sm font-semibold tracking-wide text-slate-700 hover:text-slate-950 dark:text-gray-300 dark:hover:text-white transition;
}

.mobile-link {
  @apply block py-3 px-5 text-sm font-semibold uppercase text-slate-900 hover:bg-slate-50 dark:text-gray-200 dark:hover:bg-gray-800;
}

.section-eyebrow {
  @apply flex text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500 mb-1;
}

.section-title {
  @apply text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white;
}

.sub-title {
  color: v-bind(bgColor1);
}

.accent-text {
  color: v-bind(bgColor1);
}

.accent-icon {
  color: v-bind(bgColor1);
}

.hide-scrollbar {
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.search-bar {
  @apply flex items-center gap-3 w-full rounded-2xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 px-4 py-3.5 shadow-sm no-underline;
}

.search-filter-btn {
  @apply h-9 w-9 rounded-xl inline-flex items-center justify-center shrink-0;
  background: v-bind(bgColor1);
}

.pill {
  @apply inline-flex items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-slate-700 dark:text-gray-200 transition no-underline;
}
.pill.is-active,
.pill:hover {
  color: #fff;
  border-color: transparent;
  background: v-bind(bgColor1);
}

.promo-card {
  @apply relative overflow-hidden rounded-3xl min-h-[200px] sm:min-h-[240px] shadow-lg bg-cover bg-center;
}

.promo-overlay {
  @apply absolute inset-0;
  background: linear-gradient(
    115deg,
    rgba(15, 20, 32, 0.92) 0%,
    rgba(15, 20, 32, 0.78) 48%,
    rgba(15, 20, 32, 0.45) 100%
  );
}

.promo-copy {
  @apply relative z-10 p-5 sm:p-7 flex flex-col justify-center max-w-xl;
}

.icon-cat {
  @apply flex flex-col items-center gap-2 min-w-[4.5rem] no-underline;
}

.icon-cat-avatar {
  @apply h-16 w-16 rounded-full flex items-center justify-center text-lg font-extrabold text-white shadow-md;
  background: color-mix(in srgb, v-bind(bgColor1) 88%, white);
}

.icon-cat-label {
  @apply text-[11px] font-semibold text-center text-slate-700 dark:text-gray-300 max-w-[4.5rem] line-clamp-2;
}

.kitchen-card {
  @apply min-w-[16.5rem] max-w-[18rem] md:min-w-0 md:max-w-none flex-shrink-0 rounded-3xl overflow-hidden bg-white dark:bg-gray-900 border border-slate-100 dark:border-gray-800 shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl;
}

.kitchen-media {
  @apply relative h-40 overflow-hidden;
}
.kitchen-media img {
  @apply h-full w-full object-cover;
}

.kitchen-badge {
  @apply absolute top-3 left-3 rounded-full bg-black/60 text-white text-[11px] font-bold px-2.5 py-1 backdrop-blur-sm;
}

.kitchen-body {
  @apply px-4 pb-4;
}

.strip-icon {
  @apply h-10 w-10 rounded-2xl inline-flex items-center justify-center text-white shrink-0;
  background: v-bind(bgColor1);
}

.hours-panel {
  @apply rounded-3xl overflow-hidden border border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl p-6 sm:p-8;
  background-image: radial-gradient(
    circle at top right,
    color-mix(in srgb, v-bind(bgColor1) 14%, transparent),
    transparent 45%
  );
}

.hours-panel-header {
  @apply flex items-center gap-4 mb-6 pb-5 border-b border-slate-100 dark:border-gray-800;
}

.hours-icon-wrap {
  @apply h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 shadow-md;
  background: v-bind(bgColor1);
}

.hours-item {
  @apply rounded-2xl bg-slate-50 dark:bg-gray-800/80 px-4 py-3.5 border border-slate-100 dark:border-gray-700;
}

.map-panel {
  @apply grid lg:grid-cols-2 gap-0 overflow-hidden rounded-3xl border border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl;
}

.map-panel-copy {
  @apply p-6 sm:p-8 lg:p-10 flex flex-col justify-center order-2 lg:order-1;
}

.map-frame {
  @apply relative min-h-[280px] h-72 sm:h-96 lg:h-full lg:min-h-[420px] order-1 lg:order-2;
}

.hygiene-card {
  @apply relative overflow-hidden rounded-3xl border border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl p-6 sm:p-8 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center;
  background-image: radial-gradient(
    circle at bottom left,
    color-mix(in srgb, v-bind(bgColor1) 12%, transparent),
    transparent 50%
  );
}

.hygiene-badge-wrap {
  @apply h-16 w-16 rounded-2xl flex items-center justify-center shadow-md shrink-0;
  background: v-bind(bgColor1);
}

.hygiene-copy {
  @apply min-w-0;
}

.hygiene-rating {
  @apply flex items-center justify-center md:justify-end;
}

.app-footer {
  background: linear-gradient(
    160deg,
    color-mix(in srgb, v-bind(bgColor1) 92%, #0f172a),
    #0f172a 70%
  );
}

.footer-social {
  @apply h-11 w-11 rounded-xl inline-flex items-center justify-center text-lg text-white/90 bg-white/10 hover:bg-white/20 transition;
}

.cart-badge {
  @apply absolute -top-1 -right-1 min-w-[1.15rem] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold inline-flex items-center justify-center;
}

.bottom-tab {
  @apply flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 no-underline transition-colors;
}
.bottom-tab.is-active {
  color: v-bind(bgColor1);
  font-weight: 700;
}

.sticky-header {
  @apply shadow-lg;
}

.top-notice-bar {
  font-weight: 500;
}

@keyframes subtle-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.top-notice-bar .flex-none {
  animation: subtle-pulse 2s infinite ease-in-out;
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }
  50% {
    opacity: 1;
  }
}

.wow.zoomIn {
  animation-name: zoomIn;
}

.general_grow {
  transition: transform 0.3s ease;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.3s ease-out;
}

.z-\[9999\] {
  z-index: 9999;
}

footer {
  position: relative;
}
</style>
